import { api } from "roboflex-thalamus-request-handler";
import { InjectionOrderModel } from "../model/InjectionOrderModel";
import { InjectionTryoutOsModel } from "../model/InjectionTryoutOsModel";
import { InjectionActivityModel } from "../model/InjectionActivityModel";
import { InjectionServiceTypeModel } from "../model/InjectionServiceTypeModel";
import { InjectionMachineModel } from "../model/InjectionMachineModel";
import { PersonModel } from "../model/PersonModel";
import { userLoggedIn } from "@/core/consts";

function asArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export class EditarApontamentoDatasource {
  async getActiveOps() {
    let response = await api.get("ordem-producao/injecao/existente");
    let convertedData = [];

    for (var ele in response.data) {
      convertedData.push(InjectionOrderModel.fromJson(response.data[ele]));
    }
    return convertedData.reverse();
  }

  /**
   * OPs com pelo menos um trabalho de injeção sem data fim (apontamento em aberto).
   * @returns {Promise<{ op_cod: number, opNum: string|number }[]>}
   */
  async getOpsComTrabalhoAberto() {
    const response = await api.get("ordem-producao/injecao/op-com-trabalho-aberto");
    const raw = response.data;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((row) => ({
        op_cod: row?.op_cod != null ? Number(row.op_cod) : null,
        opNum: row?.opNum ?? row?.op_num ?? null,
      }))
      .filter((row) => row.op_cod != null && !Number.isNaN(row.op_cod));
  }

  async getOps() {
    let response = await api.get("ordem-producao/injecao/pendente");
    let convertedData = [];

    for (var ele in response.data.data) {
      convertedData.push(InjectionOrderModel.fromJson(response.data.data[ele]));
    }
    return convertedData;
  }

  /** Detalhe da OP: qtProduzida = último trabalho; borra/refugo/galho = soma de todos os trabalhos. */
  async getOpDetails(opId) {
    let response = await api.get(`ordem-producao/injecao/buscar/${opId}`);
    let convertedData = InjectionOrderModel.fromJson(response.data);
    return convertedData;
  }

  async getActivities(opId) {
    let response = await api.get(`ordem-producao/trabalho/listar/${opId}`);
    let convertedData = [];

    for (var ele in response.data) {
      convertedData.push(InjectionActivityModel.fromJson(response.data[ele]));
    }

    return convertedData.reverse();
  }

  /**
   * OS tryout com pelo menos um trabalho de injeção sem data fim (apontamento em aberto).
   * @returns {Promise<{ os_id: number, codigo: string }[]>}
   */
  async getOsComTrabalhoAberto() {
    const response = await api.get("ordem-servico/injecao/os-com-trabalho-aberto");
    const raw = response.data;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((row) => ({
        os_id: row?.os_id != null ? Number(row.os_id) : row?.id != null ? Number(row.id) : null,
        codigo: row?.codigo ?? null,
      }))
      .filter((row) => row.os_id != null && !Number.isNaN(row.os_id));
  }

  /** OS tryout em andamento (listagem para seleção). */
  async getActiveOs() {
    const response = await api.get("ordem-servico/injecao/pendente");
    return asArray(response.data)
      .map((row) => InjectionTryoutOsModel.fromListJson(row))
      .reverse();
  }

  /** Detalhe da OS tryout: qtProduzida = último trabalho; borra/refugo/galho = soma. */
  async getOsDetails(osId) {
    const response = await api.get(`ordem-servico/injecao/buscar/${osId}`);
    return InjectionTryoutOsModel.fromJson(response.data);
  }

  async getActivitiesByOs(osId) {
    const response = await api.get(`ordem-servico/injecao/trabalho/listar/${osId}`);
    return asArray(response.data)
      .map((row) => InjectionActivityModel.fromJson(row))
      .reverse();
  }

  async getServicesType() {
    let response = await api.get("injecao/servico");
    let convertedData = [];

    for (let ele of response.data.data) {
      convertedData.push(InjectionServiceTypeModel.fromJson(ele));
    }

    return convertedData;
  }

  async getMachines() {
    let response = await api.get("injecao/maquina/listar");
    let convertedData = [];

    for (let ele of response.data) {
      convertedData.push(InjectionMachineModel.fromJson(ele));
    }

    return convertedData;
  }

  async listarMotivosInjecao() {
    const response = await api.get("injecao/motivo/listar");
    return Array.isArray(response.data?.data) ? response.data.data : [];
  }

  async getInjetionEmployees() {
    const response = await api.get("injecao/colaboradores");
    const rows = Array.isArray(response.data) ? response.data : [];
    return rows.map((ele) => PersonModel.fromJson(ele));
  }

  /**
   * Correção parcial de trabalho (apenas campos enviados no body).
   * @param {string|number} trabalhoId
   * @param {Record<string, unknown>} partialBody
   */
  async listarTemposTrabalho(trabalhoId) {
    const response = await api.get(`trabalho/tempo/listar/${trabalhoId}`);
    return Array.isArray(response.data?.data) ? response.data.data : [];
  }

  /**
   * Atualização parcial de um registro inj_tempo (pausa/retomada).
   * @param {string|number} tempoId
   * @param {Record<string, unknown>} patch — motivo_id, obs, data_evento
   */
  async atualizarTempoTrabalho(tempoId, patch) {
    try {
      const response = await api.put(`trabalho/tempo/atualizar/${tempoId}`, patch);
      return response.data?.data ?? null;
    } catch (e) {
      console.error("atualizarTempoTrabalho error:", e);
      const msg = e.response?.data?.error ?? e.response?.data?.msg ?? e.message;
      throw new Error(typeof msg === "string" ? msg : String(e));
    }
  }

  /** Exclusão lógica do apontamento (trabalho de injeção). */
  async excluirTrabalhoInjecao(trabalhoId) {
    const response = await api.delete(`injecao/trabalho/excluir/${trabalhoId}`);
    return response.data?.msg ?? "Apontamento excluído.";
  }

  async updateTrabalhoCorrecao(trabalhoId, partialBody) {
    try {
      const response = await api.post(`trabalho/correcao/${trabalhoId}`, partialBody);
      const trabalho = response.data?.data ?? response.data;
      return InjectionActivityModel.fromJson(trabalho);
    } catch (e) {
      console.error("updateTrabalhoCorrecao error:", e);
      const msg = e.response?.data?.error ?? e.response?.data?.msg ?? e.message;
      return typeof msg === "string" ? msg : String(e);
    }
  }

  async finishOp(opId) {
    const response = await api.post(`injecao/op/encerrar`, {
      op_cod: opId,
      usuario_id: userLoggedIn?.id,
    });
    return response.data?.msg ?? "OP finalizada com sucesso.";
  }

  async finishOs(osId) {
    const response = await api.post("ordem-servico/injecao/encerrar", { os_id: osId });
    return response.data?.msg ?? "OS finalizada com sucesso.";
  }

  async reopenOp(opId) {
    const response = await api.post(`injecao/op/reabrir`, {
      op_cod: parseInt(opId, 10),
      usuario_id: userLoggedIn?.id,
    });
    return response.data?.msg ?? "OP reaberta com sucesso.";
  }
}
